// src/components/PoliciesView.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Scale, Frown, Coins } from 'lucide-react';
import { useSimulationStore } from '../../store/useSimulationStore';
import { authHeaders } from '../../store/useAuthStore';
import { apiUrl } from '../../utils/api';
import KpiCard from '../common/KpiCard';
import Section from '../common/Section';

// Type mirrors policies.ts — no data, just the shape
type PolicyStatus = 'active' | 'draft' | 'locked' | 'unavailable';
interface Policy {
  id: string;
  icon: string;
  name: string;
  group: string;
  status: PolicyStatus;
  cost: string;
  effect: string;
  mechanic: string;
  unlock_condition?: string;
  description: string;
  strength?: number;
}

const statusConfig: Record<PolicyStatus, { label: string; className: string }> = {
  active:      { label: 'Active',      className: 'bg-green-900/50  border-green-700  text-green-300'  },
  draft:       { label: 'Draft',       className: 'bg-yellow-900/50 border-yellow-700 text-yellow-300' },
  locked:      { label: 'Locked',      className: 'bg-gray-800      border-gray-600   text-gray-500'   },
  unavailable: { label: 'Unavailable', className: 'bg-gray-800      border-gray-600   text-gray-600'   },
};

const PoliciesView: React.FC = () => {
  const nodes = useSimulationStore(s => s.nodes);
  const [policies,  setPolicies]  = useState<Policy[]>([]);
  const [selected,  setSelected]  = useState<string | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [actionMsg, setActionMsg] = useState<{ id: string; msg: string } | null>(null);
  
  const fetchPolicies = useCallback(() => {
    fetch(apiUrl('/api/policies'), { headers: authHeaders() })
       .then(r => r.json())
       .then(data => { setPolicies(data); setLoading(false); })
       .catch(() => setLoading(false));
  }, []);
  
  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);
  
  const handleAction = async (e: React.MouseEvent, id: string, action: 'enact' | 'repeal') => {
    e.stopPropagation();
    const res  = await fetch(apiUrl(`/api/policies/${id}/${action}`), { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    if (data.ok) {
      setPolicies(data.policies);          // server returns updated full list
      setActionMsg({ id, msg: action === 'enact' ? '✓ Enacted' : '✓ Repealed' });
      setTimeout(() => setActionMsg(null), 2000);
    } else {
      setActionMsg({ id, msg: `✗ ${data.error}` });
      setTimeout(() => setActionMsg(null), 3000);
    }
  };
  
  const handleStrengthChange = async (policyId: string, pct: number) => {
    const newStrength = pct / 100;
    try {
      const res = await fetch(apiUrl(`/api/policies/${policyId}/strength`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ strength: newStrength }),
      });
      const data = await res.json();
      if (data.ok) {
        setPolicies(data.policies);
        setActionMsg({ id: policyId, msg: `Strength set to ${pct}%` });
      } else {
        setActionMsg({ id: policyId, msg: data.error || 'Failed' });
      }
      setTimeout(() => setActionMsg(null), 2000);
    } catch {
      setActionMsg({ id: policyId, msg: 'Network error' });
      setTimeout(() => setActionMsg(null), 3000);
    }
  };
  
  // Group policies for display
  const groups = policies.reduce<Record<string, Policy[]>>((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
  }, {});
  
  const avgStability = nodes.length
     ? nodes.reduce((s, n) => s + (n.stability || 0), 0) / nodes.length : 0;
  const poorNodes = nodes.filter(n => n.income_status === 'poor').length;
  const richNodes = nodes.filter(n => n.income_status === 'rich').length;
  const activeCount = policies.filter(p => p.status === 'active').length;
  
  return (
     <div className="space-y-3">
       
       {/* KPI bar — matches dashboard grid style */}
       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
         <KpiCard icon={<ClipboardList className="w-4 h-4" />} label="Active Policies" value={activeCount} color="text-green-400" sub="currently enacted" />
         <KpiCard icon={<Scale className="w-4 h-4" />} label="Avg Stability" value={`${(avgStability * 100).toFixed(1)}%`} color={avgStability > 0.6 ? 'text-green-400' : avgStability > 0.35 ? 'text-yellow-400' : 'text-red-400'} />
         <KpiCard icon={<Frown className="w-4 h-4" />} label="Poor Nodes" value={poorNodes} color="text-gray-300" />
         <KpiCard icon={<Coins className="w-4 h-4" />} label="Rich Nodes" value={richNodes} color="text-yellow-300" />
       </div>
       
       {/* Policy groups */}
       {loading ? (
          <div className="text-center text-gray-500 py-12">Loading policies from server</div>
       ) : (
          Object.entries(groups).map(([group, groupPolicies]) => (
             <Section key={group} title={group}>
               <div className="space-y-2">
                 {groupPolicies.map(policy => {
                   const cfg = statusConfig[policy.status];
                   const isExpanded = selected === policy.id;
                   const feedback = actionMsg?.id === policy.id ? actionMsg.msg : null;
                   const isUnavailable = policy.status === 'locked' || policy.status === 'unavailable';
                   
                   return (
                      <div
                         key={policy.id}
                         onClick={() => setSelected(isExpanded ? null : policy.id)}
                         className={`rounded-xl p-4 cursor-pointer transition-all border ${
                            isUnavailable ? 'bg-gray-900 border-gray-800 opacity-50' :
                               isExpanded ? 'bg-gray-800 border-blue-700' :
                                  policy.status === 'active' ? 'bg-green-950/40 border-green-800 hover:border-green-600' :
                                     'bg-gray-800 border-gray-700 hover:border-gray-500'
                         }`}
                      >
                        {/* Card header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{policy.icon}</span>
                            <div>
                              <div className="text-white font-medium text-sm">{policy.name}</div>
                              <div className="text-gray-400 text-xs mt-0.5">{policy.effect}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded border ${cfg.className}`}>{cfg.label}</span>
                            <span className="text-gray-600 text-xs">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                        </div>
                        
                        {/* Expanded detail */}
                        {isExpanded && (
                           <div className="mt-4 space-y-3 border-t border-gray-700 pt-3">
                             <p className="text-gray-300 text-sm">{policy.description}</p>
                             <p className="text-gray-500 text-xs font-mono bg-gray-900 rounded p-2">{policy.mechanic}</p>
                             {policy.unlock_condition && (
                                <p className="text-yellow-600 text-xs">{policy.unlock_condition}</p>
                             )}
                             
                             {/* Strength slider */}
                             <div className="space-y-1">
                               <div className="flex items-center justify-between text-xs">
                                 <span className="text-gray-400">Policy strength</span>
                                 <span className="font-mono text-gray-300">
                                   {Math.round((policy.strength ?? 0.2) * 100)}%
                                 </span>
                               </div>
                               <input
                                  type="range"
                                  min={0}
                                  max={100}
                                  defaultValue={Math.round((policy.strength ?? 0.2) * 100)}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseUp={(e) => { e.stopPropagation(); handleStrengthChange(policy.id, Number((e.target as HTMLInputElement).value)); }}
                                  onTouchEnd={(e) => { e.stopPropagation(); handleStrengthChange(policy.id, Number((e.target as HTMLInputElement).value)); }}
                                  onChange={() => {}}
                                  className="w-full accent-blue-500"
                               />
                               <p className="text-[10px] text-gray-500">
                                 Higher strength amplifies this policy’s effect on the simulation.
                               </p>
                             </div>
                             
                             {/* Cost + actions */}
                             <div className="flex items-center justify-between">
                               <span className="text-gray-400 text-xs">Cost <span className="text-white">{policy.cost}</span></span>
                               <div className="flex items-center gap-2">
                                 {feedback && (
                                    <span className={`text-xs font-medium ${feedback.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>
                                             {feedback}
                                          </span>
                                 )}
                                 {policy.status === 'active' && (
                                    <button
                                       onClick={e => handleAction(e, policy.id, 'repeal')}
                                       className="px-3 py-1.5 text-xs font-medium bg-red-900/50 hover:bg-red-900 border border-red-700 text-red-300 rounded-lg transition-colors"
                                    >
                                      Repeal
                                    </button>
                                 )}
                                 {policy.status === 'draft' && (
                                    <button
                                       onClick={e => handleAction(e, policy.id, 'enact')}
                                       className="px-3 py-1.5 text-xs font-medium bg-blue-900/50 hover:bg-blue-900 border border-blue-700 text-blue-300 rounded-lg transition-colors"
                                    >
                                      Enact Policy
                                    </button>
                                 )}
                               </div>
                             </div>
                             
                           </div>
                        )}
                      </div>
                   );
                 })}
               </div>
             </Section>
          ))
       )}
     </div>
  );
};

export default PoliciesView;
